import {
  isNarrationSupportedBlockType,
  NARRATION_QUESTION_BLOCK_TYPES,
  type NarrationManifest,
  type NarrationManifestEntry,
  type NarrationReadyEntry,
  type StaticNarrationPromptKey,
} from '@course-authoring/shared-types';

import type { ClickExpandNarrationTarget, NarrationEvent } from './narrationEvents';

export interface NarrationOrderedBlock {
  id: string;
  type: string;
  questionDefinitionId?: number;
  description?: unknown;
  gated?: boolean;
  expandType?: 'accordion' | 'top-tabs';
  topics?: readonly { id: string }[];
  contentType?: 'text-image' | 'text-only' | 'video';
}

export type NarrationPlanKind =
  | 'continuous'
  | 'video'
  | 'policy-acknowledgement'
  | 'electronic-signature'
  | 'link'
  | 'accordion'
  | 'tabs'
  | 'comparison-text'
  | 'comparison-video'
  | 'map'
  | 'question';

export type NarrationPlanPhase =
  | 'starting'
  | 'continuous'
  | 'video-intro'
  | 'waiting-for-video'
  | 'acknowledgement-intro'
  | 'waiting-for-acknowledgement'
  | 'click-expand-intro'
  | 'topic'
  | 'comparison-intro'
  | 'comparison-option'
  | 'comparison-initial-tail'
  | 'comparison-video-prompt'
  | 'comparison-video-complete'
  | 'waiting-for-side'
  | 'map-intro'
  | 'waiting-for-region'
  | 'map-region'
  | 'waiting-for-region-close'
  | 'question-initial'
  | 'question-pre-submit-wait'
  | 'question-retry-wait'
  | 'question-post-submit'
  | 'question-final-review'
  | 'question-complete';

export type QuestionOutcomeEvent = Extract<NarrationEvent, {
  type: 'question-outcome-committed';
}>;
export type StandaloneVideoEvent = Extract<NarrationEvent, {
  type: 'video-playback-started' | 'video-playback-completed';
}>;
export type AcknowledgementEvent = Extract<NarrationEvent, { type: 'acknowledged' }>;
export type TopicEvent = Extract<NarrationEvent, { type: 'topic-selected' | 'topic-closed' }>;
export type ComparisonEvent = Extract<NarrationEvent, {
  type:
    | 'comparison-side-selected'
    | 'comparison-video-playback-started'
    | 'comparison-video-playback-completed';
}>;
export type MapEvent = Extract<NarrationEvent, {
  type: 'map-region-opened' | 'map-region-changed' | 'map-region-closed';
}>;

export type QuestionNarrationStage = 'retry-wait' | 'post-submit' | 'final-review' | 'complete';

export interface QuestionNarrationState {
  event: QuestionOutcomeEvent;
  stage: QuestionNarrationStage;
}

export interface ActiveNarrationPlan {
  token: number;
  blockId: string;
  kind: NarrationPlanKind;
  phase: NarrationPlanPhase;
  topicIds: readonly string[];
  /** Click-Expand only: the renderer target, captured by the first live lookup after intro. */
  clickExpandTarget: ClickExpandNarrationTarget | null;
  /** Click-Expand only: unreadable topics passed over by auto-advance in this plan. */
  skippedTopics: Set<number>;
  /** Click-Expand only: a learner interaction stopped the plan; only Play resumes it. */
  suspended: boolean;
  activeSide: 1 | 2;
  countedSides: Set<1 | 2>;
  activeRegionId: string | null;
  questionState: QuestionNarrationState | null;
}

export type PlaybackPurpose =
  | { kind: 'lesson-title' }
  | { kind: 'continuous-content' }
  | { kind: 'block-step'; step: string }
  | { kind: 'topic'; topicIndex: number }
  | { kind: 'comparison-option'; side: 1 | 2 }
  | { kind: 'comparison-step'; step: string; side?: 1 | 2 }
  | { kind: 'map-region'; regionId: string };

export type ActiveVideoObservation =
  | { kind: 'standalone'; blockId: string }
  | { kind: 'comparison'; blockId: string; side: 1 | 2 };

export interface NarrationSequenceStep {
  entry: NarrationManifestEntry | undefined;
  purpose: PlaybackPurpose;
}

const CONTINUOUS_NARRATION_BLOCK_TYPES = new Set([
  'basic-content-a',
  'stylized-numbered-list',
  'stylized-sidebar-callout',
  'stylized-text-callout',
  'lesson-complete',
]);

const QUESTION_NARRATION_BLOCK_TYPES = new Set<string>(NARRATION_QUESTION_BLOCK_TYPES);

export const INTERACTION_PROMPT_KEYS: Partial<
  Record<string, readonly StaticNarrationPromptKey[]>
> = {
  video: ['resume.after_video'],
  'policy-acknowledgement': ['resume.after_document_acknowledgement'],
  'electronic-signature': ['resume.after_acknowledgement'],
  link: ['resume.on_link_click'],
  'comparison-slider': ['resume.after_video', 'resume.on_other_option'],
  map: ['resume.on_region_selection'],
};

export function isClickExpandPlan(plan: ActiveNarrationPlan): boolean {
  return plan.kind === 'accordion' || plan.kind === 'tabs';
}

/** Manifest seam for a zero-based Click-Expand topic index: index 0 is `topic:1`. */
export function topicSeamKey(topicIndex: number): string {
  return `topic:${topicIndex + 1}`;
}

/**
 * The topic auto-advance tries next: the lowest index neither opened by the
 * renderer nor already passed over by this plan, or null when none remains.
 */
export function nextAutoAdvanceTopicIndex(
  topicCount: number,
  openedTopicIndices: ReadonlySet<number>,
  skippedTopics: ReadonlySet<number>,
): number | null {
  for (let index = 0; index < topicCount; index += 1) {
    if (!openedTopicIndices.has(index) && !skippedTopics.has(index)) return index;
  }
  return null;
}

export function isContinuousNarrationBlockType(type: string): boolean {
  return CONTINUOUS_NARRATION_BLOCK_TYPES.has(type);
}

export function isSkippableNarrationGap(
  block: NarrationOrderedBlock,
  narration: NarrationManifest | undefined,
): boolean {
  if (block.type === 'basic-content-b') return true;
  if (block.type === 'lesson-complete') return false;
  if (!isContinuousNarrationBlockType(block.type)) return false;
  return narration?.blocks[block.id]?.content?.status !== 'ready';
}

/**
 * The manifest status behind a skippable gap when the gap is worth reporting,
 * or `null` when it is expected. Two gaps are expected: the legacy
 * scroll-only `basic-content-b` block, which never had narration, and content
 * an author deliberately left `empty`.
 *
 * Deliberately adjacent to `isSkippableNarrationGap`: a new expected-gap case
 * added to one without the other would silently start emitting spurious skip
 * diagnostics for content that is behaving as authored.
 */
export function unexpectedNarrationGapStatus(
  block: NarrationOrderedBlock,
  narration: NarrationManifest | undefined,
): string | null {
  if (block.type === 'basic-content-b') return null;
  const status = narration?.blocks[block.id]?.content?.status;
  if (status === 'empty') return null;
  return status ?? 'absent';
}

export function isQuestionNarrationBlockType(type: string): boolean {
  return QUESTION_NARRATION_BLOCK_TYPES.has(type);
}

function hasValidQuestionReference(block: NarrationOrderedBlock): boolean {
  return !isQuestionNarrationBlockType(block.type)
    || (typeof block.questionDefinitionId === 'number'
      && Number.isInteger(block.questionDefinitionId)
      && block.questionDefinitionId > 0
      && block.description !== undefined
      && block.description !== null);
}

export function isSupportedNarrationBlock(block: NarrationOrderedBlock): boolean {
  return isNarrationSupportedBlockType(block.type) && hasValidQuestionReference(block);
}

export function questionSubmitPromptKey(): StaticNarrationPromptKey {
  return 'pause.submit_response';
}

export function planKindForBlock(block: NarrationOrderedBlock): NarrationPlanKind | null {
  if (isContinuousNarrationBlockType(block.type)) return 'continuous';
  if (block.type === 'video') return 'video';
  if (block.type === 'policy-acknowledgement') return 'policy-acknowledgement';
  if (block.type === 'electronic-signature') return 'electronic-signature';
  if (block.type === 'link') return 'link';
  if (block.type === 'click-expand') {
    return block.expandType === 'top-tabs' ? 'tabs' : 'accordion';
  }
  if (block.type === 'comparison-slider') {
    return block.contentType === 'video' ? 'comparison-video' : 'comparison-text';
  }
  if (block.type === 'map') return 'map';
  if (isQuestionNarrationBlockType(block.type) && hasValidQuestionReference(block)) {
    return 'question';
  }
  return null;
}

export function getActiveVideoDisposition(
  activeVideos: ReadonlyMap<string, ActiveVideoObservation>,
  blockId: string,
  kind: NarrationPlanKind,
): 'none' | 'own-video-wait' | 'blocked' {
  if (activeVideos.size === 0) return 'none';
  const activeVideo = activeVideos.get(blockId);
  if (kind === 'video' && activeVideo?.kind === 'standalone') return 'own-video-wait';
  if (kind === 'comparison-video' && activeVideo?.kind === 'comparison') {
    return 'own-video-wait';
  }
  return 'blocked';
}

export function isDuplicateQuestionOutcome(
  existingState: QuestionNarrationState | undefined,
  event: QuestionOutcomeEvent,
  activePlan: ActiveNarrationPlan | null,
): boolean {
  const activeQuestionReplay = activePlan?.blockId === event.blockId
    && activePlan.kind === 'question'
    && activePlan.phase !== 'question-retry-wait';
  return existingState?.event.outcome === event.outcome
    && existingState.event.presentation === event.presentation
    && !(event.outcome === 'incorrect' && activeQuestionReplay);
}

export function createActivePlan(
  token: number,
  block: NarrationOrderedBlock,
  kind: NarrationPlanKind,
  questionState: QuestionNarrationState | null = null,
): ActiveNarrationPlan {
  return {
    token,
    blockId: block.id,
    kind,
    phase: 'starting',
    topicIds: (block.topics ?? []).map((topic) => topic.id),
    clickExpandTarget: null,
    skippedTopics: new Set<number>(),
    suspended: false,
    activeSide: 1,
    countedSides: new Set<1 | 2>(),
    activeRegionId: null,
    questionState,
  };
}

export function hasReadyEntry(
  entries: Record<string, NarrationManifestEntry> | undefined,
): boolean {
  return Object.values(entries ?? {}).some((entry) => entry.status === 'ready');
}

/** The playable title entry for `lessonId`, or undefined when there is none. */
export function readyLessonTitleEntry(
  narration: NarrationManifest | undefined,
  lessonId: string | null | undefined,
): NarrationReadyEntry | undefined {
  if (lessonId === null || lessonId === undefined) return undefined;
  const entry = narration?.lessonTitles?.[lessonId];
  return entry?.status === 'ready' ? entry : undefined;
}

/** Human-readable plan step, for console diagnostics. */
export function describePlaybackPurpose(purpose: PlaybackPurpose): string {
  switch (purpose.kind) {
    case 'lesson-title':
      return 'lesson-title';
    case 'continuous-content':
      return 'content';
    case 'block-step':
      return purpose.step;
    case 'topic':
      return topicSeamKey(purpose.topicIndex);
    case 'comparison-option':
      return `option:${purpose.side}`;
    case 'comparison-step':
      return purpose.side === undefined ? purpose.step : `${purpose.step} (side ${purpose.side})`;
    case 'map-region':
      return `region:${purpose.regionId}`;
  }
}

export function assertNeverPlanKind(kind: never): never {
  throw new Error(`Unsupported narration plan kind: ${String(kind)}`);
}

export function assertNeverNarrationEvent(event: never): never {
  throw new Error(`Unsupported narration event: ${JSON.stringify(event)}`);
}
